import type { Feature, TaskType } from "./schemas.js";

/**
 * Map a feature to task types based on its AI Tasks field and category.
 */

const TASK_KEYWORDS: Record<TaskType, string[]> = {
  scaffold: ["scaffold", "setup", "init", "boilerplate", "config"],
  "api-endpoint": ["api", "endpoint", "route", "controller", "rest", "graphql"],
  "react-component": ["component", "ui", "widget", "form", "modal", "page"],
  "complex-ui": [
    "dashboard",
    "animation",
    "drag",
    "drop",
    "editor",
    "chart",
    "visualization",
  ],
  "database-schema": ["schema", "migration", "database", "table", "model", "seed"],
  integration: ["integration", "oauth", "stripe", "webhook", "third-party", "sdk"],
  "test-unit": ["test", "unit-test", "spec"],
  "test-e2e": ["e2e", "end-to-end", "playwright", "cypress"],
  devops: ["ci", "cd", "deploy", "docker", "kubernetes", "pipeline"],
  documentation: ["docs", "documentation", "readme", "guide"],
  refactoring: ["refactor", "cleanup", "optimize", "restructure"],
  architecture: ["architecture", "design", "system", "pattern"],
  debugging: ["debug", "fix", "bug", "error", "issue"],
};

const CATEGORY_DEFAULTS: Record<string, TaskType[]> = {
  infrastructure: ["scaffold", "database-schema", "devops"],
  frontend: ["react-component", "test-unit"],
  backend: ["api-endpoint", "test-unit"],
  performance: ["refactoring", "debugging"],
  "bug fix": ["debugging", "test-unit"],
  integration: ["integration", "test-unit"],
  design: ["react-component", "complex-ui"],
};

export function mapTaskTypes(feature: Feature): TaskType[] {
  const tasks = new Set<TaskType>();

  // Match AI task names against keywords
  const allText = [
    ...feature.aiTasks,
    feature.description,
    feature.category,
  ]
    .join(" ")
    .toLowerCase();

  for (const [taskType, keywords] of Object.entries(TASK_KEYWORDS)) {
    if (keywords.some((kw) => allText.includes(kw))) {
      tasks.add(taskType as TaskType);
    }
  }

  // Fall back to category defaults if no matches
  if (tasks.size === 0) {
    const categoryKey = feature.category.toLowerCase();
    const defaults = CATEGORY_DEFAULTS[categoryKey];
    if (defaults) {
      defaults.forEach((t) => tasks.add(t));
    } else {
      // Ultimate fallback
      tasks.add("react-component");
      tasks.add("test-unit");
    }
  }

  return Array.from(tasks);
}
